import { ReadonlyVec2, vec2, vec3, vec4 } from 'gl-matrix';
import { RegionOfInterest } from '../../../data/structure/RegionOfInterest';
import { PlaneView } from '../../../view/views/PlaneView';
import { DrawAction, DrawActionState } from '../DrawAction';
import { ContourTexture } from '../../../data/structure/ContourTexture';
import { View } from '../../../view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { DrawObject } from '../DrawObject';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { ViewManager } from '~/engine/view/ViewManager';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';
import { getActiveSession } from '~/domain/session/activeSession';

export enum BrushDimensions {
    Brush2D = 2,
    Brush3D = 3,
}

/**
 * {@link BrushDrawActionState} stores the state of the active brush drawing action.\
 * {@link BrushDrawActionState} is constructed when the user starts drawing and deleted when the drawing finishes.
 */
export abstract class BrushDrawActionState extends DrawActionState {
    /**
     * The size of the brush in millimeters.\
     * The brush is a circle with a diameter of {@link brushSize} millimeters.
     */
    protected readonly brushSize: number;

    /**
     * The size of the empty space around the brush in millimeters.
     */
    protected readonly margin: number;

    /**
     * Resolution of the {@link BrushDrawActionState} in mm/px.\
     * This is equal to the {@link PlaneView.resolution resolution} of the {@link view}.
     */
    protected readonly resolution: ReadonlyVec2;

    /**
     * Dimensions definition for the brush.\
     * 2D brush has the thickness of a single slice.
     */
    protected readonly dimensions: BrushDimensions;

    /**
     * {@link DrawObject} on which the user is drawing. So basically the object holding the draw buffer.\
     * It resides in the {@link targetSpace} and is used to render the user's drawing onto the {@link targetRoi}.
     */
    protected readonly drawObject: DrawObject;
    protected readonly _planeView: PlaneView;

    /**
     * {@link BrushDrawActionState} constructor, which takes the {@link PlaneView} on which the drawing action occurs,\
     * an array of {@link RegionOfInterest RegionsOfInterest} that the drawing action should modify,\
     * the {@link SliceSource} defining the space in which the {@link targets} reside,\
     * the number of dimensions of the brush and the {@link ReadonlyVec2 Client pixel coordinates} of the mouse, values in between [0, pixelSize], at the time of the event.\
     *
     * @param view {@link PlaneView} on which the drawing action occured.
     * @param targets {@link Array} of {@link RegionOfInterest RegionsOfInterest}. These are the {@link RegionOfInterest RegionsOfInterest} that the drawing action should modify.
     * @param targetSpace {@link SliceSource} defining the space in which the {@link targets} reside. This is needed to define the slices.
     * @param dimensions 2D or 3D brush.
     * @param _brushSize Diameter of the brush circle in millimeters. The brush is an SDF and so brushSize * 0.5 is the distance, at which values are 0.5.
     * @param margin Size of the empty space around the brush in millimeters. This is the distance from the brush edge, at which values become either 0 or 1.
     */
    public constructor(
        view: PlaneView,
        target: RegionOfInterest,
        targetImage: ImageObject,
        dimensions: BrushDimensions,
        brushSize: number,
        erase: boolean
    ) {
        super(view, target, targetImage, erase);
        this.brushSize = brushSize;

        // Calculate the resolution for the brush texture: the resolution of the image in the view's direction
        const viewXres = Math.abs(
            vec3.dot(view.viewXDirection, target._imageGrid.getRes())
        );
        const viewYres = Math.abs(
            vec3.dot(view.viewYDirection, target._imageGrid.getRes())
        );

        this.resolution = vec2.fromValues(viewXres, viewYres);
        this.dimensions = dimensions;
        this.margin = ContourTexture.DEFAULT_MARGIN;

        this._planeView = view;

        this.drawObject = new DrawObject(
            this.targetImage,
            this.resolution,
            view.getPatientSpaceBoundingBox(),
            vec4.fromValues(
                target.color[0],
                target.color[1],
                target.color[2],
                1
            )
        );

        view.addCustomObject(this.drawObject);
    }

    /**
     * Method to get the {@link ContourTexture} that is to be drawn onto the {@link targetRoi}.\
     * This is constructed from the user's drawing.\
     * - First the texture is read to find its dimensions.
     * - That dimension information is converted into a {@link BoundingBox}.
     * - Then a new {@link ContourTexture} is created with that optimized {@link BoundingBox}.
     * - Then the user's drawing is rendered onto the {@link ContourTexture}.
     * - And lastly that {@link ContourTexture} is returned.
     * @returns The {@link ContourTexture} that is to be drawn onto the {@link targetRoi}.
     */
    protected get contourTexture(): ContourTexture | undefined {
        return this.drawObject.toContourTexture();
    }

    /**
     * While {@link BrushDrawActionState} is active, {@link onMouseMove}-events render new data on the brush buffer.
     * @param coordinates {@link ReadonlyVec2 Client pixel coordinates} in between [0, pixelSize], of the mouse position at the time of the event.
     * @param view {@link View} on which the event occured.
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_HANDLED EVENT_HANDLED} if the event was successfully handled or\
     * {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if the mouse moved to a different {@link View}, finishing the {@link BrushDrawAction}.
     */
    public abstract onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: View
    ): EventHandlerResult;

    /**
     * Method to deallocate the WebGL memory used by this {@link BrushDrawActionState}.\
     * This {@link DrawObject.delete deletes} the {@link drawObject} used by this {@link BrushDrawAction}.\
     * This also {@link Environment.removeCustomObject removes} the {@link drawObject} from the {@link Environment}.
     *
     * Note: It is important to remember to call this {@link clearMemory}, even if it's overridden in the subclass.
     */
    public clearMemory() {
        this._planeView.removeCustomObject(this.drawObject);
        this.drawObject.delete();
    }
}

/**
 * Abstract class for {@link BrushDrawAction BrushDrawActions}.\
 * {@link BrushDrawAction BrushDrawActions} are used to provide brush like drawing functionality.\
 * The user can draw on the {@link targetRoi} with a brush of a given size.\
 * The brush is a circle with a diameter of {@link brushSize} millimeters.\
 * The user can draw by clicking and dragging the mouse.\
 * We can define different kinds of brushes by extending this class and providing a {@link BrushDrawActionState} implementation.
 */
export abstract class BrushDrawAction extends DrawAction {
    /**
     * Diameter of the brush circle in millimeters.\
     * If the brush is 3D, this is its size along all 3 axes.
     */
    protected brushSize: number;

    protected _erase: boolean;

    /**
     * BrushDrawAction constructor.
     * @param brushSize Diameter of the brush circle in millimeters. If the brush is 3D, this is its size along all 3 axes.
     * @param drawButton Mouse button that starts the drawing action.
     * @param cancelButton Mouse button that cancels an active drawing action.
     * @param modifierKeys Optional {@link ModifierKeys} that must be pressed in order to activate the drawing action.
     */
    public constructor(
        brushSize: number,
        erase: boolean,
        drawButton: number,
        cancelButton: number,
        modifierKeys?: ModifierKeys
    ) {
        super(drawButton, cancelButton, modifierKeys);
        this.brushSize = brushSize;
        this._erase = erase;
    }

    /**
     * Method to inform the {@link BrushDrawAction} that the mouse button was released.
     *
     * If {@link activeEventState} was defined, meaning that the {@link BrushDrawAction} was active
     * and the {@link button} was the {@link drawButton},\
     * the {@link BrushDrawActionState.applyBoolean()} is called, which will apply the drawing to {@link BrushDrawActionState.targets}.\
     * Then {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} is returned, which will finish the {@link BrushDrawAction}.
     *
     * Note that the {@link EventHandlerResult} in this case has {@link EventHandlerResult.stopPropagation stopPropagation} set to false,\
     * so that the event may be processed by {@link BrushCursorAction}, which will activate itself right after mouse button is released.\
     *
     * Otherwise {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} is returned.
     * @param button Mouse button that was released.
     * @param _coordinates {@link ReadonlyVec2 Client pixel coordinates} in between [0, pixelSize], of the mouse position at the time of the event.
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.FINISH_ACTION FINISH_ACTION} if the {@link BrushDrawAction} was active and the {@link button} was the {@link drawButton}.\
     * {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED} otherwise.
     */
    public onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        return this.stopAction();
    }

    public stopAction(): EventHandlerResult {
        if (this._state) {
            const roiNumber = this._state.targetStructure.roiNumber;
            // Capture the env's active SS before clearMemory / clearing
            // this._state — once the state is gone we lose the view.
            const activeStructureSet =
                this._state.view.environment.structureSet?.parent;
            this._state.applyBoolean();
            const bb = this._state.targetStructure.boundingBox;
            this._state.clearMemory();
            this._state = undefined;

            ViewManager.viewUpdateEvent(
                new VolumeViewUpdateEvent(bb, LayerBit.CONTOUR_LAYER_BIT, true)
            );

            // New path: notify ContourService so the domain change tracker
            // records a changeset entry for the persistence / bridge layer.
            // The engine operates on a single active StructureSet at a
            // time (env.structureSet); resolve the roiNumber against that
            // SS specifically, not by scanning every loaded SS — roiNumber
            // is only unique within a structure set.
            // The existing legacy event (RoiContoursModifiedEvent) that was
            // published inside applyBoolean → TextureWrite.finish() continues
            // to fire unchanged — we are ADDING a call, not replacing one.
            const session = getActiveSession();
            if (session && activeStructureSet) {
                const roi = activeStructureSet.rois.find(
                    (r) => r.roiNumber === roiNumber
                );
                if (roi) {
                    // modifiedSlices is not yet tracked at the draw-action
                    // level; passing an empty array is safe — ContourService
                    // only uses it to record which slices changed, which is
                    // not yet consumed by any downstream subscriber.
                    session.contourService.commitContourEdit(
                        { id: roi.id },
                        []
                    );
                }
            }

            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }
}
